import type { IApplicationState } from '@bridebook/web/lib/types';
import type { z } from 'zod/v4';
import type { ClientActionAccess, ClientActionAuthor, RawActionOutcome } from '../definition';

// Any action creator returning a plain `{ type, payload }` Redux action.
type AnyActionCreator = (...args: never[]) => { type: string | number; payload: unknown };

// Props typed `T | undefined` become optional: omitting a prop and sending `undefined` are the
// same action at runtime, so a raw dispatch may skip them.
type UndefinedToOptional<T> = {
  [K in keyof T as undefined extends T[K] ? never : K]: T[K];
} & {
  [K in keyof T as undefined extends T[K] ? K : never]?: T[K];
};

/**
 * Checks a raw entry's schema against the action's payload. The schema output must fit the
 * payload (narrower is fine) and may not add keys, so a payload change on the creator breaks
 * the entry. Resolves to `unknown` (no-op in an intersection) when it fits, else to an error.
 */
type RawSchemaCheck<S extends z.ZodType, Action extends { payload: unknown }> =
  z.output<S> extends UndefinedToOptional<Action['payload']>
    ? Exclude<keyof z.output<S>, keyof Action['payload']> extends never
      ? unknown
      : ['schema sends keys the action payload does not have']
    : ['schema payload is not assignable to the action payload'];

/**
 * Defines a raw-dispatch lexicon entry, typed by a Redux action creator's return type (passed
 * type-only): `rawAction<typeof updateWeddingField>()({ ... })`. `type` must be that action's
 * Redux `type`, and the zod schema is checked against its `payload` here. One raw entry per
 * Redux action: payload variants live in the schema, and `access` may depend on the payload.
 * - `extras`: payload fields the app adds for the couple at dispatch. They must not come from
 *   the schema: the payload is parsed twice (server, then browser), and a schema that adds a
 *   key would reject its own output the second time.
 * - `outcome`: the follow-up actions the epic emits. The dispatch waits for them and reports
 *   the result.
 * - `inForce`: true when the payload changes nothing in the live store. The action then
 *   settles as `unchanged` before any card or dispatch.
 */
export const rawAction =
  <Creator extends AnyActionCreator>() =>
  <S extends z.ZodType>(definition: {
    type: ReturnType<Creator>['type'];
    description: string;
    access: ClientActionAccess | ((payload: z.output<S>) => ClientActionAccess);
    inForce?: (payload: z.output<S>, state: IApplicationState) => boolean;
    authoredBy?: ClientActionAuthor;
    alwaysAllow?: false;
    schema: S & RawSchemaCheck<S, ReturnType<Creator>>;
    extras?: Partial<ReturnType<Creator>['payload']>;
    outcome?: RawActionOutcome;
  }): {
    kind: 'raw';
    type: ReturnType<Creator>['type'];
    description: string;
    access: ClientActionAccess | ((payload: z.output<S>) => ClientActionAccess);
    inForce?: (payload: z.output<S>, state: IApplicationState) => boolean;
    authoredBy?: ClientActionAuthor;
    alwaysAllow?: false;
    schema: S;
    extras?: Partial<ReturnType<Creator>['payload']>;
    outcome?: RawActionOutcome;
  } => ({ kind: 'raw', ...definition });
